// [COPIAR ARQUIVO]

/* GS://PROTOCOLO
 * Arquivo: C:\Users\Usuario\GoldenSisters_Project\goldensisters_admin_web\src\pages\assembly\AssemblyOrderComponentsPage.js
 * Dossiê de Projeto e Colaboração: Ecossistema GoldenSisters
 * Versão: 1.2 – Tela C – Componentes da Ordem (FASE 4 FECHADA).
 * Impacto da Versão: [src\pages\assembly\AssemblyOrderComponentsPage.js]
 * Data: 29/11/2025
 * Responsável: ChatGPT (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - Mantido fluxo de cadastro/edição de componentes da Ordem de Montagem.
 * - Mantida integração com addAssemblyComponents/clearAssemblyComponents no serviço.
 * - Carrega componentes já existentes para edição; sobrescreve a “receita” ao salvar.
 * - Ajustados textos para deixar claro que, nesta fase, não há movimento automático em estoque/PDV.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { GSM_COLLECTIONS } from '../../services/gs_nf_module_dataModel';
import {
  addAssemblyComponents,
  clearAssemblyComponents,
} from '../../services/gs_nf_module_service';
import {
  ArrowLeft,
  ClipboardList,
  AlertCircle,
  CheckCircle2,
  PlusCircle,
  Trash2,
  Loader,
} from 'lucide-react';

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    if (value.toDate) {
      return value.toDate().toLocaleString('pt-BR');
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('pt-BR');
  } catch {
    return '-';
  }
};

export default function AssemblyOrderComponentsPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const loadData = async () => {
      if (!orderId) {
        setError('Ordem de Montagem não informada.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      setSuccessMessage('');

      try {
        // Carrega a Ordem de Montagem
        const orderRef = doc(
          collection(db, GSM_COLLECTIONS.ASSEMBLIES),
          orderId
        );
        const orderSnap = await getDoc(orderRef);

        if (!orderSnap.exists()) {
          setError('Ordem de Montagem não encontrada.');
          setOrder(null);
          setComponents([]);
          return;
        }

        const data = orderSnap.data() || {};
        setOrder({
          id: orderSnap.id,
          status: data.status || 'ABERTA',
          orderReference: data.orderReference || '',
          finalSku: data.finalSku || '',
          orderDescription: data.orderDescription || '',
          orderQuantity: data.orderQuantity || 1,
          assemblyType: data.assemblyType || 'SIMPLES',
          createdAt: data.createdAt || null,
        });

        // Carrega componentes existentes, se houver
        const compsColRef = collection(
          db,
          GSM_COLLECTIONS.ASSEMBLIES,
          orderId,
          'components'
        );
        const compsSnap = await getDocs(compsColRef);
        if (compsSnap.empty) {
          setComponents([
            {
              id: null,
              description: '',
              componentRole: '',
              qtyPerSet: 1,
              lotId: '',
              lotItemId: '',
              notes: '',
            },
          ]);
        } else {
          const loaded = compsSnap.docs.map((docSnap) => {
            const c = docSnap.data() || {};
            return {
              id: docSnap.id,
              description: c.description || '',
              componentRole: c.componentRole || '',
              qtyPerSet: c.qtyPerSet || 1,
              lotId: c.lotId || '',
              lotItemId: c.lotItemId || '',
              notes: c.notes || '',
            };
          });
          setComponents(loaded);
        }
      } catch (err) {
        console.error('Erro ao carregar componentes da Ordem de Montagem:', err);
        setError('Não foi possível carregar os componentes da Ordem.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [orderId]);

  const handleBack = () => {
    navigate('/assembly/orders');
  };

  const handleComponentChange = (index, field, value) => {
    setComponents((prev) =>
      prev.map((comp, i) =>
        i === index
          ? {
              ...comp,
              [field]:
                field === 'qtyPerSet' ? (Number(value) || 0) : value,
            }
          : comp
      )
    );
  };

  const handleAddRow = () => {
    setComponents((prev) => [
      ...prev,
      {
        id: null,
        description: '',
        componentRole: '',
        qtyPerSet: 1,
        lotId: '',
        lotItemId: '',
        notes: '',
      },
    ]);
  };

  const handleRemoveRow = (index) => {
    setComponents((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!orderId) return;

    setError('');
    setSuccessMessage('');

    // Validação simples
    const sanitized = [];
    for (const comp of components) {
      const description = String(comp.description || '').trim();
      const qtyPerSet = Number(comp.qtyPerSet) || 0;

      if (!description) {
        setError(
          'Todos os componentes devem ter uma descrição preenchida.'
        );
        return;
      }
      if (qtyPerSet <= 0) {
        setError(
          'Todos os componentes devem ter quantidade por conjunto (qtyPerSet) maior que zero.'
        );
        return;
      }

      sanitized.push({
        description,
        componentRole: String(comp.componentRole || '').trim(),
        qtyPerSet,
        lotId: String(comp.lotId || '').trim() || null,
        lotItemId: String(comp.lotItemId || '').trim() || null,
        notes: String(comp.notes || '').trim(),
      });
    }

    if (sanitized.length === 0) {
      setError('Informe ao menos um componente para esta montagem.');
      return;
    }

    try {
      setSaving(true);

      // Remove componentes antigos e grava a nova “receita”
      await clearAssemblyComponents(orderId);
      await addAssemblyComponents(orderId, sanitized, {});

      setSuccessMessage('Componentes da Ordem de Montagem salvos com sucesso.');
    } catch (err) {
      console.error('Erro ao salvar componentes da Ordem de Montagem:', err);
      setError(
        'Não foi possível salvar os componentes. Verifique o console para detalhes.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-brand-black px-6 py-6">
      {/* Cabeçalho + voltar */}
      <div className="flex flex-col gap-4 mb-6">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center text-sm text-gray-600 hover:text-brand-gold hover:underline w-fit"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Voltar para Ordens de Montagem
        </button>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="flex items-center text-2xl font-semibold text-brand-gold">
              <ClipboardList className="w-7 h-7 mr-3" />
              Componentes da Ordem de Montagem
            </h1>
            <p className="mt-1 text-sm text-gray-600 max-w-2xl">
              Definição da “receita” da montagem: quais peças entram em cada
              conjunto e em que quantidade. Nesta fase a montagem ainda não
              movimenta estoque automaticamente; isso será tratado nas fases
              seguintes do planejamento.
            </p>
          </div>

          {order && (
            <div className="text-sm text-right space-y-1">
              <div className="text-xs text-gray-500">
                Ordem:
                <span className="ml-1 text-gray-900 font-semibold">
                  {order.orderReference || '—'}
                </span>
              </div>
              {order.finalSku && (
                <div className="text-xs text-gray-500">
                  SKU final:
                  <span className="ml-1 text-gray-900 font-mono">
                    {order.finalSku}
                  </span>
                </div>
              )}
              <div className="text-xs text-gray-500">
                Criada em:
                <span className="ml-1 text-gray-900">
                  {formatDateTime(order.createdAt)}
                </span>
              </div>
              <div className="text-xs text-gray-500">
                Qtd. conjuntos:
                <span className="ml-1 text-gray-900 font-medium">
                  {order.orderQuantity || 1}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Alertas */}
      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Conteúdo principal */}
      {loading ? (
        <div className="flex items-center justify-center py-10 text-gray-700">
          <Loader className="w-6 h-6 mr-2 animate-spin text-brand-gold" />
          Carregando dados da Ordem de Montagem...
        </div>
      ) : !order ? (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <span>Ordem de Montagem não encontrada.</span>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">
                Componentes por conjunto
              </h2>
              <p className="text-xs text-gray-600 mt-1">
                Preencha a lista abaixo com cada peça que compõe o conjunto de
                montagem, informando a quantidade por conjunto (qty/conjunto).
              </p>
            </div>

            <button
              type="button"
              onClick={handleAddRow}
              className="inline-flex items-center px-3 py-1.5 text-xs rounded-lg bg-brand-gold text-brand-black font-semibold shadow hover:brightness-110 transition-colors"
            >
              <PlusCircle className="w-3 h-3 mr-1.5" />
              Adicionar componente
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">
                    Descrição da peça
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">
                    Função / Papel
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">
                    Qtd. por conjunto
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">
                    Lote (opcional)
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">
                    Item do lote (opcional)
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">
                    Observações
                  </th>
                  <th className="px-4 py-2 text-right font-semibold text-gray-700">
                    Ação
                  </th>
                </tr>
              </thead>
              <tbody>
                {components.map((comp, index) => (
                  <tr
                    key={index}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-4 py-2 align-top w-64">
                      <input
                        type="text"
                        className="w-full rounded-lg border border-gray-300 px-2 py-1 text-xs text-gray-900 focus:outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold"
                        placeholder="Ex.: Colar base PRONTO, argolas, extensor..."
                        value={comp.description}
                        onChange={(e) =>
                          handleComponentChange(
                            index,
                            'description',
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td className="px-4 py-2 align-top w-40">
                      <input
                        type="text"
                        className="w-full rounded-lg border border-gray-300 px-2 py-1 text-xs text-gray-900 focus:outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold"
                        placeholder="Ex.: COLAR, BRINCO, PINGENTE..."
                        value={comp.componentRole}
                        onChange={(e) =>
                          handleComponentChange(
                            index,
                            'componentRole',
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td className="px-4 py-2 align-top w-24">
                      <input
                        type="number"
                        min={1}
                        className="w-full rounded-lg border border-gray-300 px-2 py-1 text-xs text-gray-900 focus:outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold"
                        value={comp.qtyPerSet}
                        onChange={(e) =>
                          handleComponentChange(
                            index,
                            'qtyPerSet',
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td className="px-4 py-2 align-top w-40">
                      <input
                        type="text"
                        className="w-full rounded-lg border border-gray-300 px-2 py-1 text-xs text-gray-900 focus:outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold"
                        placeholder="ID do Lote (se conhecido)"
                        value={comp.lotId}
                        onChange={(e) =>
                          handleComponentChange(
                            index,
                            'lotId',
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td className="px-4 py-2 align-top w-40">
                      <input
                        type="text"
                        className="w-full rounded-lg border border-gray-300 px-2 py-1 text-xs text-gray-900 focus:outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold"
                        placeholder="ID do item do lote"
                        value={comp.lotItemId}
                        onChange={(e) =>
                          handleComponentChange(
                            index,
                            'lotItemId',
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td className="px-4 py-2 align-top w-56">
                      <textarea
                        className="w-full min-h-[40px] rounded-lg border border-gray-300 px-2 py-1 text-xs text-gray-900 focus:outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold resize-y"
                        placeholder="Observações específicas deste componente na montagem."
                        value={comp.notes}
                        onChange={(e) =>
                          handleComponentChange(
                            index,
                            'notes',
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td className="px-4 py-2 align-top text-right">
                      <button
                        type="button"
                        onClick={() => handleRemoveRow(index)}
                        className="inline-flex items-center px-2 py-1 text-xs rounded-lg border border-red-300 text-red-700 bg-white hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Remover
                      </button>
                    </td>
                  </tr>
                ))}

                {components.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-6 text-center text-xs text-gray-500"
                    >
                      Nenhum componente informado. Clique em “Adicionar
                      componente” para começar a montar a receita.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 text-sm rounded-lg bg-brand-gold text-brand-black font-semibold shadow-md hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Salvar componentes
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
